/**
 * Pictogrammes, couleurs de balisage et mise en forme des nombres du plan
 * des pistes (conventions des plans de station français).
 */
import { BedDouble, Coffee, Cross, GraduationCap, ShoppingBag, Ticket, Truck, Utensils, type LucideIcon } from 'lucide-preact';
import type { Difficulty } from '@snowchain/shared';
import type { PictoKind } from './overlay';

/** Couleurs de balisage des pistes. */
export const PISTE_COLOR: Record<Difficulty, string> = {
  verte: '#1f9a4b',
  bleue: '#1d62c4',
  rouge: '#d32630',
  noire: '#18191b',
};

export const WORKS_COLOR = '#8b9096';

/** Fond et glyphe de chaque pictogramme. */
export const PICTO_STYLE: Record<PictoKind, { bg: string; icon: LucideIcon | null; text?: string }> = {
  restaurant: { bg: '#8a4b22', icon: Utensils },
  snack: { bg: '#a8652b', icon: Coffee },
  secours: { bg: '#c62828', icon: Cross },
  ecole: { bg: '#b8432f', icon: GraduationCap },
  location: { bg: '#2e5d74', icon: ShoppingBag },
  caisses: { bg: '#2e5d74', icon: Ticket },
  parking: { bg: '#1d62c4', icon: null, text: 'P' },
  hebergement: { bg: '#5b4a86', icon: BedDouble },
  garage: { bg: '#465864', icon: Truck },
};

/** Pictogramme SVG (carré arrondi coloré, glyphe blanc), centré en (x, y). */
export function PictoGlyph({ kind, x, y, size = 15 }: { kind: PictoKind; x: number; y: number; size?: number }) {
  const st = PICTO_STYLE[kind];
  const Icon = st.icon;
  const g = size * 0.68;
  return (
    <g>
      <rect x={x - size / 2} y={y - size / 2} width={size} height={size} rx={3} fill={st.bg} stroke="#fff" stroke-width={1.3} />
      {Icon ? (
        <Icon x={x - g / 2} y={y - g / 2} width={g} height={g} color="#fff" strokeWidth={2.4} />
      ) : (
        <text x={x} y={y + size * 0.3} text-anchor="middle" class="tm-picto-letter" font-size={size * 0.78}>
          {st.text}
        </text>
      )}
    </g>
  );
}

const nf1 = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1, minimumFractionDigits: 1 });
const nf0 = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });

/** Kilomètres avec une décimale (« 2,1 km »). */
export function km(m: number): string {
  return `${nf1.format(m / 1000)} km`;
}

/** Mètres arrondis (« 1 240 m »). */
export function meters(m: number): string {
  return `${nf0.format(Math.round(m))} m`;
}

/** Longueur courte : mètres sous 1 km, sinon kilomètres. */
export function lengthLabel(m: number): string {
  return m < 1000 ? meters(m) : km(m);
}
